import React from "react";
import PropTypes from "prop-types";
import LinearProgress from "@material-ui/core/LinearProgress";
import { connect } from "react-redux";
import { createTheme, MuiThemeProvider } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";

import { fetchCharge } from "../../actions/charge";
import {
  resetRedirection,
  resetRedirectionParams
} from "../../actions/redirect";
import { Status } from "../../model/Charge";
import PaymentForm from "../../components/payment/PaymentForm";
import PaymentResult from "../../components/payment/result/PaymentResult";
import "./PaymentPage.css";

export class PaymentPage extends React.Component {
  static closeIframe() {
    const event = { type: "lpg.iframe.close" };
    window.parent.postMessage(event, "*");
  }

  constructor(props) {
    super(props);
    this.getAttValueFromTheme = this.getAttValueFromTheme.bind(this);
  }

  componentDidMount() {
    const { match, dispatch, history } = this.props;
    const { params } = match;

    /**
     * Fetch and append 3ds kit JavaScript
     */
    const loadThreeDsKitScript = () => {
      const url = window.location.toString();
      const scriptId = "threeDsKit";
      const isStaging = url.includes("staging");

      // Determine the script URL and integrity based on the environment and URL conditions
      let scriptUrl = process.env.REACT_APP_DEFAULT_URL;
      let integrity = process.env.REACT_APP_DEFAULT_INTEGRITY;

      if (isStaging) {
        integrity = process.env.REACT_APP_THREEDSKIT_STAGING_INTEGRITY;
      } else if (
        [
          process.env.REACT_APP_THREEDSKIT_PAYMENT_URL_FORM,
          process.env.REACT_APP_THREEDSKIT_PAYMENT_URL_API,
          process.env.REACT_APP_THREEDSKIT_PAYMENT_URL_STATIC
        ].some(envUrl => url.includes(envUrl))
      ) {
        scriptUrl = process.env.REACT_APP_THREEDSKIT_URL;
        integrity = process.env.REACT_APP_THREEDSKIT_INTEGRITY_ATTRIBUTE;
      }

      // Avoid adding the script if it already exists
      if (document.getElementById(scriptId)) return;

      // Create and append the script
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = scriptUrl;
      script.type = "text/javascript";
      script.integrity = integrity;
      script.crossOrigin = "anonymous";
      script.async = true;

      document.body.appendChild(script);
    };

    loadThreeDsKitScript();

    /**
     * fetch charge data
     */
    dispatch(fetchCharge(params.id));
    window.addEventListener("message", event => {
      const { data } = event;
      if (data && data.type && data.type === "lpg.iframe.close") {
        dispatch(resetRedirectionParams());
        dispatch(resetRedirection());
        dispatch(fetchCharge(params.id));
        history.push(`/charge/${params.id}`);
      }
    });
  }

  getAttValueFromTheme(currentValue, firstAttribute, secondAttribute) {
    const { charge } = this.props;
    const { theme } = charge;
    return theme &&
      theme.palette &&
      theme.palette[firstAttribute] &&
      theme.palette[firstAttribute][secondAttribute]
      ? theme.palette[firstAttribute][secondAttribute]
      : currentValue;
  }

  render() {
    const { error, isLoading, charge, match } = this.props;
    if (error) {
      return <span>{error}</span>;
    }

    const theme = createTheme({
      typography: {
        fontSize: 16
      },
      palette: {
        primary: {
          light: `${this.getAttValueFromTheme("#29337A", "primary", "light")}`,
          main: `${this.getAttValueFromTheme("#20285f", "primary", "main")}`,
          dark: `${this.getAttValueFromTheme("#171C43", "primary", "dark")}`
        },
        secondary: {
          main: `${this.getAttValueFromTheme("#3775ba", "secondary", "main")}`
        },
        error: {
          main: `${this.getAttValueFromTheme("#db504a", "error", "main")}`
        },
        background: {
          default: `${this.getAttValueFromTheme(
            "#ecebe4",
            "background",
            "default"
          )}`,
          paper: `${this.getAttValueFromTheme("#fff", "background", "paper")}`
        },
        text: {
          primary: `${this.getAttValueFromTheme(
            "rgba(0, 0, 0, 0.87)",
            "text",
            "primary"
          )}`,
          secondary: `${this.getAttValueFromTheme(
            "rgba(0, 0, 0, 0.54)",
            "text",
            "secondary"
          )}`
        },
        success: {
          main: `${this.getAttValueFromTheme("#000000", "success", "main")}`
        }
      }
    });

    let componentToDisplay;
    switch (charge.status) {
      case Status.PAID:
      case Status.PROVISIONED:
      case Status.DROPPED:
      case Status.DOUBLE_SPEND:
      case Status.LATE_SPEND:
      case Status.REFUNDED:
      case Status.REFUNDED_PARTIALLY:
        componentToDisplay = <PaymentResult charge={charge} />;
        break;
      case Status.DUE:
        componentToDisplay = <PaymentForm charge={charge} match={match} />;
        break;
      default:
        componentToDisplay = <span>Error: charge state is unknown</span>;
        break;
    }

    if (window.location.hostname.indexOf("lyra") < 0) {
      document.title =
        charge.theme && charge.theme.domainName
          ? charge.theme.domainName.title
          : "Lyra";
      const faviconHref =
        charge.theme && charge.theme.domainName
          ? `${window.location.origin}/favicon-${charge.theme.domainName.image}.ico`
          : `${window.location.origin}/favicon.ico`;
      document.getElementById("favicon").setAttribute("href", faviconHref);
    }

    return (
      <MuiThemeProvider theme={theme}>
        <Paper id="payment-page">
          {isLoading ? <LinearProgress /> : componentToDisplay}
          {window.location !== window.parent.location && (
            <button
              id="lpg-modal-close"
              type="button"
              onClick={PaymentPage.closeIframe}
            >
              ×
            </button>
          )}
        </Paper>
      </MuiThemeProvider>
    );
  }
}

PaymentPage.propTypes = {
  error: PropTypes.string,
  isLoading: PropTypes.bool,
  charge: PropTypes.shape({
    status: PropTypes.string,
    theme: PropTypes.shape()
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired
    })
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  dispatch: PropTypes.func.isRequired
};

PaymentPage.defaultProps = {
  isLoading: true,
  error: undefined,
  charge: {}
};

function mapStateToProps({ chargeReducer }) {
  return chargeReducer;
}

export default connect(mapStateToProps)(PaymentPage);
